import { useCallback, useEffect, useState } from 'react';
import type { Nullable, StringMap } from 'corgi-types';
import type { ArticleGalleryImage } from 'graphql-ts-types';

import { useOpenConfigurationTrap } from '../../../product-mvc-buttons/components/configuration-trap/hooks/drawer';
import {
  dispatchProductVariantClickEvent,
  dispatchSaveButtonClickEvent,
} from '../../../product-mvc-buttons/components/tracking';
import type { AvailableGroup, Group } from '../../../product-mvc-buttons/components/types';
import { useLocalization } from '../../providers/LocaleProvider';
import type { DispatchState } from '../../types/React';
import type { Change, SelectedValue } from './types';

const useGroupValuePreSelect = (
  groups: Group[],
  setSelectedValue: DispatchState<Nullable<SelectedValue>>
) => {
  useEffect(() => {
    groups.forEach(group => {
      const preSelectedValue = group.values.find(({ selected, url }) => selected && url);

      if (preSelectedValue && preSelectedValue.url) {
        setSelectedValue({
          url: preSelectedValue.url,
          title: preSelectedValue.title,
          variantKey: preSelectedValue.key,
        });
      }
    });
  }, [groups]);
};

const useFormSubmit = (
  selectedValue: Nullable<SelectedValue>,
  selectedValues: StringMap<string>
) => {
  const openConfigurationTrap = useOpenConfigurationTrap();

  return () => {
    if (selectedValue) {
      dispatchSaveButtonClickEvent();
      if ('alternativeVariantValue' in selectedValue) {
        openConfigurationTrap({ ...selectedValue, selectedValues });
      } else {
        window.location.href = selectedValue.url;
      }
    }
  };
};

const useSelectedValue = () => {
  const [value, setValue] = useState<Nullable<SelectedValue>>(null);

  return { selectedValue: value, setSelectedValue: setValue };
};

const filterGroups = (groups: Group[], value: SelectedValue) =>
  groups
    .flatMap(group => group.values)
    .findIndex(item =>
      'alternativeVariantValue' in value
        ? value.alternativeVariantValue === item.key
        : item.url === value.url
    );

export const useDrawer = (
  groups: Group[],
  selectedValues: StringMap<string>,
  listLength: number
) => {
  const { t } = useLocalization();
  const { selectedValue, setSelectedValue } = useSelectedValue();
  const onChange = useCallback(
    ({ value }: Change) => {
      if (value) {
        dispatchProductVariantClickEvent(value.title, listLength, filterGroups(groups, value));
      }
      setSelectedValue(value);
    },
    [setSelectedValue]
  );
  const onSubmit = useFormSubmit(selectedValue, selectedValues);

  useGroupValuePreSelect(groups, setSelectedValue);

  return {
    selectedValue,
    form: { ctaLabel: t('h24_select'), onChange, onSubmit },
  };
};

export const useSelectedValueImages = (
  groups: AvailableGroup[],
  selectedValue: Nullable<SelectedValue>
) => {
  const images = groups.reduce((acc: ArticleGalleryImage[], { values }) => {
    const foundValue = values.find(
      value => value.key === selectedValue?.variantKey && value?.article
    );

    return foundValue?.article ? [...acc, ...foundValue.article.images] : acc;
  }, []);

  const fabricSample =
    groups
      .flatMap(({ values }) => values)
      .find(value => value.key === selectedValue?.variantKey && value?.article)?.article
      ?.fabricSample?.article?.images?.[0].path || null;

  return { images, fabricSample };
};
